import { useEffect, useState } from "react";
import { X, Export, PlusSquare } from "@phosphor-icons/react";

const KEY = "hg_install_dismissed_at";
const SNOOZE_DAYS = 14;

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isPhone = () => window.matchMedia?.("(max-width: 1023px)").matches && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
const snoozed = () => Date.now() - Number(localStorage.getItem(KEY) || 0) < SNOOZE_DAYS * 864e5;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(() => window.__hgInstallEvent || null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); window.__hgInstallEvent = e; setDeferred(e); };
    const onInstalled = () => setShow(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  useEffect(() => {
    setShow(!isStandalone() && !snoozed() && isPhone() && (isIOS() || !!deferred));
  }, [deferred]);

  if (!show) return null;
  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setShow(false); };
  const install = async () => {
    if (isIOS()) { setIosHelp(true); return; }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    window.__hgInstallEvent = null; setDeferred(null);
    if (outcome === "accepted") setShow(false);
  };

  return <div data-testid="install-prompt" className="fixed inset-x-3 z-40 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] lg:bottom-3 bg-primary text-primary-foreground border-l-8 border-accent shadow-2xl p-4">
    <button onClick={dismiss} data-testid="btn-install-dismiss" aria-label="Not now" className="absolute top-2 right-2 p-1 opacity-70 hover:opacity-100"><X size={18}/></button>
    {!iosHelp ? <div className="flex items-center gap-3 pr-6">
      <img src="/icons/icon-192.png" alt="" className="w-10 h-10 shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold leading-tight">Install HaulGuard</div>
        <div className="text-xs opacity-80 leading-snug">Full screen, right from your home screen.</div>
      </div>
      <button onClick={install} data-testid="btn-install-app" className="btn-amber shrink-0 !px-3">Install</button>
    </div> : <div className="pr-6" data-testid="install-ios-help">
      <div className="font-display font-bold mb-2">Add HaulGuard to your home screen</div>
      <ol className="text-sm space-y-1.5">
        <li>1. Tap the Share button <Export size={18} weight="bold" className="inline -mt-1 text-accent"/> at the bottom of Safari</li>
        <li>2. Choose <b>Add to Home Screen</b> <PlusSquare size={18} weight="bold" className="inline -mt-1 text-accent"/></li>
        <li>3. Tap <b>Add</b>. HaulGuard appears with its own icon.</li>
      </ol>
      <div className="text-[11px] opacity-70 mt-2">On iPhone this works in Safari. In another browser, open this page in Safari first.</div>
    </div>}
  </div>;
}
